import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon, SearchIcon, CheckCircleIcon, PackageIcon, ScanLineIcon, PlayIcon, PauseIcon, XCircleIcon, ClockIcon, Leaf, DropletIcon, FlaskConicalIcon, ScaleIcon, CookingPotIcon } from 'lucide-react';
interface MultiheadWeigherModuleProps {
  onBackToOverview?: () => void;
}
interface Material {
  id: string;
  name: string;
  type: 'vegetable' | 'protein' | 'starch' | 'other';
  quantity: number;
  unit: string;
  location: string;
  scanned: boolean;
  ready: boolean; // New property to track if material is ready from pre-process
}
interface WeigherOrder {
  id: string;
  name: string;
  status: 'pending' | 'in-progress' | 'completed' | 'paused';
  deadline: Date;
  startTime?: Date;
  duration?: number; // in seconds
  materials: Material[];
}
export function MultiheadWeigherModule({
  onBackToOverview
}: MultiheadWeigherModuleProps) {
  const [view, setView] = useState<'list' | 'detail' | 'scan' | 'production'>('list');
  const [selectedOrder, setSelectedOrder] = useState<WeigherOrder | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [scannedCode, setScannedCode] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanSuccess, setScanSuccess] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);
  const [materialToScan, setMaterialToScan] = useState<Material | null>(null);
  const [showScanPopup, setShowScanPopup] = useState(false);
  const [scanningInProgress, setScanningInProgress] = useState(false);
  const [scanPopupSuccess, setScanPopupSuccess] = useState<boolean | null>(null);
  const [scanPopupError, setScanPopupError] = useState('');
  const [scanPopupCode, setScanPopupCode] = useState('');
  // Sample orders with some materials marked as ready
  const [weigherOrders, setWeigherOrders] = useState<WeigherOrder[]>([{
    id: 'W1001',
    name: 'Gustoria Paella',
    status: 'pending',
    deadline: new Date(new Date().getTime() + 45 * 60000),
    materials: [{
      id: 'V001',
      name: 'Erbsen',
      type: 'vegetable',
      quantity: 15.5,
      unit: 'kg',
      location: 'Gemüselager B2',
      scanned: false,
      ready: true // Ready from pre-process
    }, {
      id: 'V002',
      name: 'Paprika gewürfelt',
      type: 'vegetable',
      quantity: 12.0,
      unit: 'kg',
      location: 'Gemüselager B3',
      scanned: false,
      ready: true
    }, {
      id: 'P001',
      name: 'Hühnerfleisch gewürfelt',
      type: 'protein',
      quantity: 20.0,
      unit: 'kg',
      location: 'Kühllager D1',
      scanned: false,
      ready: false
    }, {
      id: 'S001',
      name: 'Reis vorgekocht',
      type: 'starch',
      quantity: 25.0,
      unit: 'kg',
      location: 'Trockenlager C1',
      scanned: false,
      ready: true
    }]
  }, {
    id: 'W1002',
    name: 'Gustoria Risotto',
    status: 'in-progress',
    deadline: new Date(new Date().getTime() + 120 * 60000),
    startTime: new Date(new Date().getTime() - 20 * 60000),
    duration: 20 * 60,
    materials: [{
      id: 'V003',
      name: 'Pilze gewürfelt',
      type: 'vegetable',
      quantity: 10.0,
      unit: 'kg',
      location: 'Gemüselager B1',
      scanned: true,
      ready: true
    }, {
      id: 'V004',
      name: 'Zucchini gewürfelt',
      type: 'vegetable',
      quantity: 8.0,
      unit: 'kg',
      location: 'Gemüselager B4',
      scanned: true,
      ready: true
    }, {
      id: 'S002',
      name: 'Risottoreis vorgekocht',
      type: 'starch',
      quantity: 22.0,
      unit: 'kg',
      location: 'Trockenlager C2',
      scanned: true,
      ready: true
    }, {
      id: 'O001',
      name: 'Parmesan gerieben',
      type: 'other',
      quantity: 5.0,
      unit: 'kg',
      location: 'Kühllager D3',
      scanned: true,
      ready: true
    }]
  }, {
    id: 'W1003',
    name: 'Gustoria Pasta Primavera',
    status: 'completed',
    deadline: new Date(new Date().getTime() - 60 * 60000),
    startTime: new Date(new Date().getTime() - 120 * 60000),
    duration: 60 * 60,
    materials: [{
      id: 'V005',
      name: 'Brokkoli blanchiert',
      type: 'vegetable',
      quantity: 12.0,
      unit: 'kg',
      location: 'Gemüselager B5',
      scanned: true,
      ready: true
    }, {
      id: 'V006',
      name: 'Karotten gewürfelt',
      type: 'vegetable',
      quantity: 10.0,
      unit: 'kg',
      location: 'Gemüselager B2',
      scanned: true,
      ready: true
    }, {
      id: 'S003',
      name: 'Nudeln vorgekocht',
      type: 'starch',
      quantity: 20.0,
      unit: 'kg',
      location: 'Trockenlager C3',
      scanned: true,
      ready: true
    }, {
      id: 'O002',
      name: 'Pinienkerne geröstet',
      type: 'other',
      quantity: 3.0,
      unit: 'kg',
      location: 'Trockenlager C4',
      scanned: true,
      ready: true
    }]
  }]);
  // Filter orders based on search term
  const filteredOrders = weigherOrders.filter(order => order.name.toLowerCase().includes(searchTerm.toLowerCase()) || order.id.includes(searchTerm));
  // Handle order selection
  const handleOrderSelect = (order: WeigherOrder) => {
    setSelectedOrder(order);
    setView('detail');
  };
  // Handle back to list
  const handleBackToList = () => {
    setView('list');
    setSelectedOrder(null);
    stopTimer();
  };
  // Handle back to detail
  const handleBackToDetail = () => {
    setView('detail');
  };
  // Handle scan mode
  const handleScanMode = () => {
    setView('scan');
    setScannedCode('');
    setScanSuccess(null);
    setErrorMessage('');
  };
  // Handle scan
  const handleScan = () => {
    if (!scannedCode || !selectedOrder) {
      setErrorMessage('Bitte geben Sie einen Barcode ein oder scannen Sie ihn');
      setScanSuccess(false);
      return;
    }
    setIsScanning(true);
    // Simulate scanning process
    setTimeout(() => {
      // Check if the scanned code matches any material
      const materialIndex = selectedOrder.materials.findIndex(material => scannedCode.includes(material.id) || scannedCode.toLowerCase().includes(material.name.toLowerCase()));
      if (materialIndex !== -1 && !selectedOrder.materials[materialIndex].scanned) {
        // Update the scanned status
        const updatedOrder = {
          ...selectedOrder
        };
        updatedOrder.materials[materialIndex].scanned = true;
        setSelectedOrder(updatedOrder);
        // Update in the orders list
        const updatedOrders = weigherOrders.map(order => order.id === updatedOrder.id ? updatedOrder : order);
        setWeigherOrders(updatedOrders);
        setScanSuccess(true);
        setErrorMessage('');
      } else if (materialIndex !== -1 && selectedOrder.materials[materialIndex].scanned) {
        setScanSuccess(false);
        setErrorMessage('Dieses Material wurde bereits gescannt!');
      } else {
        setScanSuccess(false);
        setErrorMessage('Der gescannte Code gehört zu keinem benötigten Material.');
      }
      setScannedCode('');
      setIsScanning(false);
    }, 1000);
  };
  // Check if all materials are scanned
  const allMaterialsScanned = selectedOrder ? selectedOrder.materials.every(material => material.scanned) : false;
  // Start production
  const handleStartProduction = () => {
    if (!selectedOrder) return;
    const updatedOrder = {
      ...selectedOrder
    };
    updatedOrder.status = 'in-progress';
    updatedOrder.startTime = new Date();
    updatedOrder.duration = 0;
    setSelectedOrder(updatedOrder);
    // Update in the orders list
    const updatedOrders = weigherOrders.map(order => order.id === updatedOrder.id ? updatedOrder : order);
    setWeigherOrders(updatedOrders);
    startTimer();
    setView('list');
  };
  // Pause production
  const handlePauseProduction = () => {
    if (!selectedOrder) return;
    const updatedOrder = {
      ...selectedOrder
    };
    if (updatedOrder.status === 'paused') {
      updatedOrder.status = 'in-progress';
      startTimer();
    } else {
      updatedOrder.status = 'paused';
      stopTimer();
    }
    setSelectedOrder(updatedOrder);
    // Update in the orders list
    const updatedOrders = weigherOrders.map(order => order.id === updatedOrder.id ? updatedOrder : order);
    setWeigherOrders(updatedOrders);
  };
  // Complete production
  const handleCompleteProduction = () => {
    if (!selectedOrder) return;
    const updatedOrder = {
      ...selectedOrder
    };
    updatedOrder.status = 'completed';
    setSelectedOrder(updatedOrder);
    // Update in the orders list
    const updatedOrders = weigherOrders.map(order => order.id === updatedOrder.id ? updatedOrder : order);
    setWeigherOrders(updatedOrders);
    stopTimer();
    setView('list');
  };
  // Timer functions
  const startTimer = () => {
    if (timerInterval) clearInterval(timerInterval);
    const interval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
    setTimerInterval(interval);
  };
  const stopTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
  };
  // Update timer when viewing an in-progress order
  useEffect(() => {
    if (selectedOrder && selectedOrder.status === 'in-progress') {
      if (selectedOrder.duration !== undefined) {
        setElapsedTime(selectedOrder.duration);
      }
      startTimer();
    } else {
      stopTimer();
    }
    return () => {
      stopTimer();
    };
  }, [selectedOrder]);
  // Update order duration
  useEffect(() => {
    if (selectedOrder && (selectedOrder.status === 'in-progress' || selectedOrder.status === 'paused')) {
      const updatedOrder = {
        ...selectedOrder,
        duration: elapsedTime
      };
      // Update in the orders list without triggering re-renders
      setWeigherOrders(prev => prev.map(order => order.id === updatedOrder.id ? updatedOrder : order));
    }
  }, [elapsedTime]);
  // Format time for display
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor(seconds % 3600 / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  // Material type icons
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'vegetable':
        return <Leaf size={16} className="text-green-600" data-id="element-939" />;
      case 'protein':
        return <PackageIcon size={16} className="text-red-600" data-id="element-940" />;
      case 'starch':
        return <DropletIcon size={16} className="text-amber-600" data-id="element-941" />;
      default:
        return <FlaskConicalIcon size={16} className="text-purple-600" data-id="element-942" />;
    }
  };
  // Handle opening the scan popup for a specific material
  const handleOpenScanPopup = (material: Material) => {
    setMaterialToScan(material);
    setShowScanPopup(true);
    setScanPopupCode('');
    setScanPopupSuccess(null);
    setScanPopupError('');
  };
  // Handle closing the scan popup
  const handleCloseScanPopup = () => {
    setShowScanPopup(false);
    setMaterialToScan(null);
    setScanPopupSuccess(null);
  };
  // Handle scanning in the popup
  const handleScanInPopup = () => {
    if (!scanPopupCode || !materialToScan || !selectedOrder) {
      setScanPopupError('Bitte geben Sie einen Barcode ein oder scannen Sie ihn');
      setScanPopupSuccess(false);
      return;
    }
    setScanningInProgress(true);
    // Simulate scanning process
    setTimeout(() => {
      // Check if the scanned code matches the material
      if (scanPopupCode.includes(materialToScan.id) || scanPopupCode.toLowerCase().includes(materialToScan.name.toLowerCase())) {
        // Update the scanned status
        const updatedOrder = {
          ...selectedOrder
        };
        const materialIndex = updatedOrder.materials.findIndex(m => m.id === materialToScan.id);
        if (materialIndex !== -1) {
          updatedOrder.materials[materialIndex].scanned = true;
          setSelectedOrder(updatedOrder);
          // Update in the orders list
          const updatedOrders = weigherOrders.map(order => order.id === updatedOrder.id ? updatedOrder : order);
          setWeigherOrders(updatedOrders);
          setScanPopupSuccess(true);
          setScanPopupError('');
          // Close popup after success
          setTimeout(() => {
            handleCloseScanPopup();
          }, 1500);
        }
      } else {
        setScanPopupSuccess(false);
        setScanPopupError('Der gescannte Code passt nicht zu diesem Material.');
      }
      setScanningInProgress(false);
    }, 1000);
  };
  return <div className="container mx-auto" data-id="element-943">
      {/* Header */}
      <div className="mb-8" data-id="element-944">
        <h2 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-945">Mehrkopfwaage</h2>
        <div className="flex items-center justify-between" data-id="element-946">
          <p className="text-gray-600" data-id="element-947">
            Präzise Verwiegung mit der Mehrkopfwaage
          </p>
          {onBackToOverview}
        </div>
      </div>
      {/* Order List View */}
      {view === 'list' && <div className="bg-white rounded-xl shadow-md p-6" data-id="element-948">
          <div className="mb-6" data-id="element-949">
            <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-950">
              Heutige Verwiegungsaufträge
            </h3>
            <p className="text-gray-600" data-id="element-951">
              Wählen Sie einen Auftrag aus, um mit dem Verwiegungsprozess zu
              beginnen
            </p>
          </div>
          {/* Search bar */}
          <div className="relative mb-6" data-id="element-952">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-953">
              <SearchIcon size={18} className="text-gray-400" data-id="element-954" />
            </div>
            <input type="text" className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md" placeholder="Nach Auftrag suchen..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-955" />
          </div>
          {/* Orders list */}
          <div className="space-y-4" data-id="element-956">
            {filteredOrders.length > 0 ? filteredOrders.map(order => <div key={order.id} className={`border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer 
                    ${order.status === 'completed' ? 'bg-green-50 border-green-200' : order.status === 'in-progress' ? 'bg-blue-50 border-blue-200' : order.status === 'paused' ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`} onClick={() => handleOrderSelect(order)} data-id="element-957">
                  <div className="flex justify-between items-start" data-id="element-958">
                    <div data-id="element-959">
                      <h4 className="font-medium text-gray-900" data-id="element-960">
                        {order.name}
                      </h4>
                      <p className="text-sm text-gray-500" data-id="element-961">
                        Auftrags-Nr: #{order.id}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-962">
                        Bereitstellung bis:{' '}
                        {order.deadline.toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}{' '}
                        Uhr
                      </p>
                      {(order.status === 'in-progress' || order.status === 'paused') && order.duration !== undefined && <p className="text-sm text-blue-600 flex items-center mt-1" data-id="element-963">
                            <ClockIcon size={14} className="mr-1" data-id="element-964" />
                            Laufzeit: {formatTime(order.duration)}
                          </p>}
                    </div>
                    <div className="flex items-center" data-id="element-965">
                      {order.status === 'completed' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-966">
                          <CheckCircleIcon size={12} className="mr-1" data-id="element-967" />
                          Abgeschlossen
                        </span> : order.status === 'in-progress' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-968">
                          <PlayIcon size={12} className="mr-1" data-id="element-969" />
                          In Bearbeitung
                        </span> : order.status === 'paused' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800" data-id="element-970">
                          <PauseIcon size={12} className="mr-1" data-id="element-971" />
                          Pausiert
                        </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-972">
                          <ScaleIcon size={12} className="mr-1" data-id="element-973" />
                          Ausstehend
                        </span>}
                    </div>
                  </div>
                  {/* Materials progress */}
                  <div className="mt-3" data-id="element-974">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-975">
                      Materialien:{' '}
                      {order.materials.filter(m => m.scanned).length} von{' '}
                      {order.materials.length} bereitgestellt
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-976">
                      <div className="bg-[#8DC63F] h-2.5 rounded-full" style={{
                width: `${order.materials.filter(m => m.scanned).length / order.materials.length * 100}%`
              }} data-id="element-977"></div>
                    </div>
                  </div>
                </div>) : <div className="text-center py-10 bg-gray-50 rounded-lg border border-gray-200" data-id="element-978">
                <ScaleIcon size={40} className="mx-auto text-gray-400 mb-3" data-id="element-979" />
                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-980">
                  Keine Verwiegungsaufträge
                </h3>
                <p className="text-gray-500" data-id="element-981">
                  Für heute sind keine Verwiegungsaufträge geplant.
                </p>
              </div>}
          </div>
        </div>}
      {/* Order Detail View */}
      {view === 'detail' && selectedOrder && <div className="bg-white rounded-xl shadow-md p-6" data-id="element-982">
          <button onClick={handleBackToList} className="flex items-center gap-2 mb-6 text-gray-600 hover:text-gray-800" data-id="element-983">
            <ArrowLeftIcon size={16} data-id="element-984" />
            <span data-id="element-985">Zurück zur Übersicht</span>
          </button>
          <div className="mb-6" data-id="element-986">
            <div className="flex justify-between items-start" data-id="element-987">
              <div data-id="element-988">
                <h3 className="text-xl font-bold text-gray-800 mb-1" data-id="element-989">
                  {selectedOrder.name}
                </h3>
                <p className="text-gray-600" data-id="element-990">
                  Auftrags-Nr: #{selectedOrder.id} | Bereitstellung bis:{' '}
                  {selectedOrder.deadline.toLocaleTimeString('de-DE', {
                hour: '2-digit',
                minute: '2-digit'
              })}{' '}
                  Uhr
                </p>
                {(selectedOrder.status === 'in-progress' || selectedOrder.status === 'paused') && selectedOrder.duration !== undefined && <p className="text-sm text-blue-600 flex items-center mt-1" data-id="element-991">
                      <ClockIcon size={14} className="mr-1" data-id="element-992" />
                      Laufzeit: {formatTime(selectedOrder.duration)}
                    </p>}
              </div>
              <div data-id="element-993">
                {selectedOrder.status === 'completed' ? <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-994">
                    <CheckCircleIcon size={16} className="mr-1" data-id="element-995" />
                    Abgeschlossen
                  </span> : selectedOrder.status === 'in-progress' ? <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800" data-id="element-996">
                    <PlayIcon size={16} className="mr-1" data-id="element-997" />
                    In Bearbeitung
                  </span> : selectedOrder.status === 'paused' ? <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800" data-id="element-998">
                    <PauseIcon size={16} className="mr-1" data-id="element-999" />
                    Pausiert
                  </span> : <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800" data-id="element-1000">
                    <ScaleIcon size={16} className="mr-1" data-id="element-1001" />
                    Ausstehend
                  </span>}
              </div>
            </div>
          </div>
          {/* Materials list */}
          <div className="mb-6" data-id="element-1002">
            <h4 className="text-lg font-semibold text-gray-800 mb-3" data-id="element-1003">
              Benötigte Materialien
            </h4>
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-4" data-id="element-1004">
              <div className="text-sm text-gray-600 mb-2" data-id="element-1005">
                Fortschritt:{' '}
                {selectedOrder.materials.filter(m => m.scanned).length} von{' '}
                {selectedOrder.materials.length} Materialien bereitgestellt
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4" data-id="element-1006">
                <div className="bg-[#8DC63F] h-2.5 rounded-full" style={{
              width: `${selectedOrder.materials.filter(m => m.scanned).length / selectedOrder.materials.length * 100}%`
            }} data-id="element-1007"></div>
              </div>
              <div className="space-y-3" data-id="element-1008">
                {selectedOrder.materials.map(material => <div key={material.id} className={`p-4 rounded-lg border ${material.scanned ? 'bg-green-50 border-green-200' : material.ready ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`} data-id="element-1009">
                    <div className="flex justify-between" data-id="element-1010">
                      <div data-id="element-1011">
                        <div className="flex items-center" data-id="element-1012">
                          {getTypeIcon(material.type)}
                          <h5 className="font-medium text-gray-900 ml-2" data-id="element-1013">
                            {material.name}
                          </h5>
                          {material.ready && !material.scanned && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full" data-id="element-1014">
                              Bereit
                            </span>}
                        </div>
                        <p className="text-sm text-gray-500 mt-1" data-id="element-1015">
                          {material.quantity} {material.unit} • Artikel-Nr:{' '}
                          {material.id}
                        </p>
                      </div>
                      <div className="flex flex-col items-end" data-id="element-1016">
                        {material.scanned ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1017">
                            <CheckCircleIcon size={12} className="mr-1" data-id="element-1018" />
                            Hinzugefügt
                          </span> : material.ready ? <button onClick={() => handleOpenScanPopup(material)} className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded-md font-medium" data-id="element-1019">
                            Rohstoff einscannen
                          </button> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-1020">
                            <PackageIcon size={12} className="mr-1" data-id="element-1021" />
                            Ausstehend
                          </span>}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Action buttons */}
          <div className="flex flex-wrap gap-3 justify-center" data-id="element-1022">
            {selectedOrder.status === 'pending'}
            {selectedOrder.status === 'pending' && <button onClick={handleStartProduction} disabled={!selectedOrder.materials.some(m => m.scanned) || allMaterialsScanned} className={`bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${!selectedOrder.materials.some(m => m.scanned) ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-1023">
                <PlayIcon size={18} data-id="element-1024" />
                <span data-id="element-1025">Produktion starten</span>
              </button>}
            {selectedOrder.status === 'pending' && allMaterialsScanned && <button onClick={handleStartProduction} className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2" data-id="element-1026">
                <PlayIcon size={18} data-id="element-1027" />
                <span data-id="element-1028">Produktion starten</span>
              </button>}
            {selectedOrder.status === 'in-progress' && <>
                <button onClick={handlePauseProduction} className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2" data-id="element-1029">
                  <PauseIcon size={18} data-id="element-1030" />
                  <span data-id="element-1031">Produktion pausieren</span>
                </button>
                <button onClick={handleCompleteProduction} className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2" data-id="element-1032">
                  <CheckCircleIcon size={18} data-id="element-1033" />
                  <span data-id="element-1034">Produktion abschließen</span>
                </button>
              </>}
            {selectedOrder.status === 'paused' && <>
                <button onClick={handlePauseProduction} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2" data-id="element-1035">
                  <PlayIcon size={18} data-id="element-1036" />
                  <span data-id="element-1037">Produktion fortsetzen</span>
                </button>
                <button onClick={handleCompleteProduction} className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2" data-id="element-1038">
                  <CheckCircleIcon size={18} data-id="element-1039" />
                  <span data-id="element-1040">Produktion abschließen</span>
                </button>
              </>}
            {selectedOrder.status === 'completed' && <div className="text-center w-full" data-id="element-1041">
                <p className="text-green-600 font-medium" data-id="element-1042">
                  Dieser Auftrag wurde erfolgreich abgeschlossen.
                </p>
              </div>}
          </div>
        </div>}
      {/* Scan Materials View */}
      {view === 'scan' && selectedOrder && <div className="bg-white rounded-xl shadow-md p-6" data-id="element-1043">
          <button onClick={handleBackToDetail} className="flex items-center gap-2 mb-6 text-gray-600 hover:text-gray-800" data-id="element-1044">
            <ArrowLeftIcon size={16} data-id="element-1045" />
            <span data-id="element-1046">Zurück zum Auftrag</span>
          </button>
          <div className="mb-6" data-id="element-1047">
            <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1048">
              Materialien scannen für {selectedOrder.name}
            </h3>
            <p className="text-gray-600" data-id="element-1049">
              Bitte scannen Sie die Materialien, die Sie zur Mehrkopfwaage
              gebracht haben.
            </p>
          </div>
          {/* Scan area */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6" data-id="element-1050">
            <h4 className="font-medium text-gray-800 mb-4 flex items-center" data-id="element-1051">
              <ScanLineIcon size={20} className="mr-2" data-id="element-1052" />
              Materialien scannen
            </h4>
            <div className="flex flex-col md:flex-row gap-3 mb-4" data-id="element-1053">
              <div className="relative flex-grow" data-id="element-1054">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1055">
                  <ScanLineIcon size={18} className="text-gray-400" data-id="element-1056" />
                </div>
                <input type="text" className={`pl-10 pr-4 py-2 w-full border ${scanSuccess === true ? 'border-green-500 bg-green-50' : scanSuccess === false ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-md`} placeholder="Barcode scannen..." value={scannedCode} onChange={e => setScannedCode(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleScan()} data-id="element-1057" />
              </div>
              <button onClick={handleScan} disabled={isScanning} className={`py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium ${isScanning ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-1058">
                {isScanning ? 'Scanne...' : 'Scannen'}
              </button>
            </div>
            {scanSuccess === true && <div className="mb-4 flex items-center text-green-600 text-sm" data-id="element-1059">
                <CheckCircleIcon size={16} className="mr-1" data-id="element-1060" />
                <span data-id="element-1061">Material erfolgreich gescannt!</span>
              </div>}
            {scanSuccess === false && <div className="mb-4 flex items-center text-red-600 text-sm" data-id="element-1062">
                <XCircleIcon size={16} className="mr-1" data-id="element-1063" />
                <span data-id="element-1064">{errorMessage}</span>
              </div>}
            {/* Materials checklist */}
            <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1065">
              <h5 className="font-medium text-gray-800 mb-3" data-id="element-1066">
                Material-Checkliste:
              </h5>
              <div className="space-y-2" data-id="element-1067">
                {selectedOrder.materials.map(material => <div key={material.id} className={`flex items-center p-3 rounded-lg border ${material.scanned ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`} data-id="element-1068">
                    <div className={`w-5 h-5 rounded-full mr-3 flex items-center justify-center ${material.scanned ? 'bg-green-500 text-white' : 'border border-gray-400'}`} data-id="element-1069">
                      {material.scanned && <CheckCircleIcon size={14} data-id="element-1070" />}
                    </div>
                    <div className="flex-1" data-id="element-1071">
                      <div className="flex items-center" data-id="element-1072">
                        {getTypeIcon(material.type)}
                        <div className="font-medium ml-2" data-id="element-1073">{material.name}</div>
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-1074">
                        {material.quantity} {material.unit} • Artikel-Nr:{' '}
                        {material.id}
                      </div>
                    </div>
                    <div data-id="element-1075">
                      {material.scanned ? <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full" data-id="element-1076">
                          Bereitgestellt
                        </span> : <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full" data-id="element-1077">
                          Ausstehend
                        </span>}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Action buttons */}
          <div className="flex justify-center" data-id="element-1078">
            {allMaterialsScanned ? <button onClick={handleStartProduction} className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2" data-id="element-1079">
                <PlayIcon size={18} data-id="element-1080" />
                <span data-id="element-1081">Produktion starten</span>
              </button> : <button onClick={handleBackToDetail} className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium" data-id="element-1082">
                Zurück zum Auftrag
              </button>}
          </div>
        </div>}
      {/* Scan Material Popup */}
      {showScanPopup && materialToScan && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1083">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4" data-id="element-1084">
            <div className="flex justify-between items-center mb-4" data-id="element-1085">
              <h3 className="text-lg font-bold text-gray-800" data-id="element-1086">
                Material einscannen
              </h3>
              <button onClick={handleCloseScanPopup} className="text-gray-500 hover:text-gray-700" data-id="element-1087">
                <XCircleIcon size={20} data-id="element-1088" />
              </button>
            </div>
            <div className="mb-4" data-id="element-1089">
              <div className="flex items-center mb-2" data-id="element-1090">
                {getTypeIcon(materialToScan.type)}
                <p className="font-medium text-gray-900 ml-2" data-id="element-1091">
                  {materialToScan.name}
                </p>
              </div>
              <p className="text-sm text-gray-600" data-id="element-1092">
                Bitte scannen Sie den Barcode für {materialToScan.name}{' '}
                (Artikel-Nr: {materialToScan.id})
              </p>
            </div>
            <div className="mb-4" data-id="element-1093">
              <div className="flex gap-2" data-id="element-1094">
                <input type="text" className={`flex-1 px-3 py-2 border rounded-md ${scanPopupSuccess === true ? 'border-green-500 bg-green-50' : scanPopupSuccess === false ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} placeholder="Barcode scannen..." value={scanPopupCode} onChange={e => setScanPopupCode(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleScanInPopup()} data-id="element-1095" />
                <button onClick={handleScanInPopup} disabled={scanningInProgress} className={`px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md ${scanningInProgress ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-1096">
                  {scanningInProgress ? 'Scanne...' : 'Scannen'}
                </button>
              </div>
              {scanPopupSuccess === true && <div className="mt-2 text-green-600 flex items-center" data-id="element-1097">
                  <CheckCircleIcon size={16} className="mr-1" data-id="element-1098" />
                  <span data-id="element-1099">Material erfolgreich gescannt!</span>
                </div>}
              {scanPopupSuccess === false && <div className="mt-2 text-red-600 flex items-center" data-id="element-1100">
                  <XCircleIcon size={16} className="mr-1" data-id="element-1101" />
                  <span data-id="element-1102">{scanPopupError}</span>
                </div>}
            </div>
          </div>
        </div>}
    </div>;
}